
import {
  computed,
  defineComponent, onMounted, Ref, ref, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useMainStore } from '@/store/mainStore';
import AppBottomPopUp from '@/components/BottomPopUp/AppBottomPopUp.vue';
import AppLabel from '@/components/AppLabel.vue';
import AppLabelInput from '@/components/UI/AppLabelInput.vue';
import DriveSharedBar from '@/modules/drive/components/DriveSharedBar.vue';
import ItemCase from '@/modules/drive/components/items/ItemCase.vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { BOTTOM_POPUP_TYPES, PHOTO_VIEW_TYPE } from '@/types/enums';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';

export default defineComponent({
  components: {
    AppBottomPopUp,
    AppLabel,
    AppLabelInput,
    ItemCase,
    DriveSharedBar,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const mainStore = useMainStore();
    const driveStore = useDriveStore();

    const isEditLabels: Ref<boolean> = ref(false);

    const isPhoto = computed(() => mainStore.BottomPopUpType === BOTTOM_POPUP_TYPES.PHOTO_INFO);
    const isFile = computed(() => mainStore.BottomPopUpType === BOTTOM_POPUP_TYPES.FILE_INFO);
    const cases = computed(() => (isPhoto.value ? driveStore.appPhotoViewMedia?.whereUsed : driveStore.appPhotoViewMedia?.whereUsed) || []); // TODO review this shit
    const isShared = computed(() => driveStore.appPhotoViewMedia?.ownerId.internalId !== mainStore.user.accountId
                                        && driveStore.appPhotoViewMedia?.ownerId.internalId !== mainStore.boundCorporate.internalId);

    const isNetwork = computed(() => route.meta.isNetwork || false);

    const sharedTo = computed(() => {
      if (driveStore.appPhotoViewMedia?.share) {
        return driveStore.appPhotoViewMedia?.share.sharedAccounts.length + driveStore.appPhotoViewMedia?.share.sharedGroups.length;
      }
      return false;
    });

    const currentLabels = computed(() => {
      if (mainStore.inputPhotoViewTags.length) {
        return mainStore.inputPhotoViewTags;
      }
      if (isPhoto.value) {
        return mainStore.inputPhotoTags;
      }
      if (isFile.value) {
        return mainStore.inputFileTags;
      }
      return mainStore.inputCaseTags;
    });

    function hidePopUp() {
      mainStore.BottomPopUpType = null;
    }

    function formatDate(date: number): string {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'long', day: '2-digit', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
      });
    }

    const fileOwner = computed(() => {
      if (isPhoto.value) {
        return driveStore.appPhotoViewMedia!.owner;
      }
      return driveStore.appPhotoViewMedia!.owner;
    });

    const authorPhotoUrl = computed(() => {
      if (isPhoto.value && driveStore.appPhotoViewMedia!.owner.photo?.url) {
        return lowResUrlByViewTypeByUrl(driveStore.appPhotoViewMedia!.owner.photo?.url, PHOTO_VIEW_TYPE.WHSML);
      }
      if (!isPhoto.value && driveStore.appPhotoViewMedia!.owner.photo?.url) {
        return lowResUrlByViewTypeByUrl(driveStore.appPhotoViewMedia!.owner.photo?.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    const createdDate = computed(() => {
      if (isPhoto.value) {
        return formatDate(driveStore.appPhotoViewMedia!.creationDate);
      }
      return formatDate(driveStore.appPhotoViewMedia!.creationDate);
    });
    const sharedDate = computed(() => {
      if (isPhoto.value && driveStore.appPhotoViewMedia?.sharedTime) {
        return formatDate(driveStore.appPhotoViewMedia.sharedTime);
      }
      if (driveStore.appPhotoViewMedia?.sharedTime) {
        return formatDate(driveStore.appPhotoViewMedia?.sharedTime);
      }
      return '';
    });

    const mediaSize = computed(() => {
      const size = driveStore.appPhotoViewMedia?.size as number | undefined;
      if (!size) {
        return 'N/A';
      }
      if (size < 1024) {
        return `${size} B`;
      }
      if (size < 1024 * 1024) {
        const sizeInKB = (size / 1024).toFixed(2);
        return `${sizeInKB} KB`;
      }
      const sizeInMB = (size / (1024 * 1024)).toFixed(2);
      return `${sizeInMB} MB`;
    });
    const updatedDate = computed(() => ' no info');

    function addLabels() {
      if (isEditLabels.value) {
        driveStore.saveFileLabels(isPhoto.value);
      }
      isEditLabels.value = !isEditLabels.value;
    }

    function goToCase(caseId: string) {
      router.push(`/drive/case/my/${caseId}/view/?fromPhoto=${driveStore.appPhotoViewMedia?.id}`).then(() => {
        driveStore.appPhotoViewMedia = undefined;
        driveStore.appPhotoViewMedia = undefined;
        mainStore.BottomPopUpType = null;
      });
    }

    watch(() => driveStore.appPhotoViewMedia!.labels, () => {
      mainStore.lablesToTagsInput(driveStore.appPhotoViewMedia!.labels, true);
    });

    onMounted(() => {
      mainStore.lablesToTagsInput(isPhoto.value ? driveStore.appPhotoViewMedia!.labels : driveStore.appPhotoViewMedia!.labels, isFile.value, isPhoto.value);
    });

    return {
      mainStore,
      sharedTo,
      isEditLabels,
      isPhoto,
      isShared,
      createdDate,
      updatedDate,
      sharedDate,
      fileOwner,
      authorPhotoUrl,
      cases,
      hidePopUp,
      formatDate,
      addLabels,
      goToCase,
      isNetwork,
      currentLabels,
      mediaSize,
    };
  },
});
